import { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router';
import z from 'zod';

import { Button } from '@components/primitives';

import { AppIcon } from '@components/features/Apps/AppIcon';
import { Restricted } from '@components/features/Restricted';
import { CreateAppForm } from '@components/features/TeamApp/CreateAppForm';
import { useCurrentTeam } from '@queries/user';
import { useLocalTeamState } from '@routes/RedirectToFirstTeam';
import { useStrongParams } from '@utils/params';

import css from './styles.module.css';

const teamRootParams = z.object({
  teamUuid: z.string(),
});

export function TeamRoot() {
  const navigate = useNavigate();
  const { teamUuid } = useStrongParams(teamRootParams);
  const { team } = useCurrentTeam();
  const apps = team?.apps || [];

  const teamState = useLocalTeamState(teamUuid);
  const app = useMemo(() => {
    const firstApp = apps.at(0);
    if (!teamState) return firstApp;
    const match = apps.find((app) => app.uuid === teamState.lastAppUuid);
    return match ?? firstApp;
  }, [teamState, apps]);

  if (app) {
    return <Navigate to={app.uuid} replace />;
  }

  return (
    <div className={css.container}>
      <div className={css.card}>
        <div className={css.icon}>
          <AppIcon app={{ sandbox: false }} aria-hidden />
        </div>

        <h1 className={css.title}>Create your first app</h1>

        <p className={css.description}>
          Apps separate your environments and keys for your secure data
          workflows
        </p>

        <CreateAppForm
          teamUuid={teamUuid}
          onCreate={(app) => navigate(app.uuid)}
        >
          <CreateAppForm.Fields />

          <div className={css.footer}>
            <Restricted
              allow="devOrAdmin"
              teamUuid={teamUuid}
              render={
                <CreateAppForm.Submit render={<Button variant="primary" />} />
              }
            >
              Create app
            </Restricted>
          </div>
        </CreateAppForm>
      </div>
    </div>
  );
}
